import { CAPTURE_EXCLUDE_ATTR } from "./screenshot.js";

// REUSABLE draw-on-image overlay (plain canvas, zero deps). The captured
// screenshot fills the ENTIRE viewport, sitting directly on top of the existing
// content (no card, no backdrop, no padding). Controls float on top: a compact
// pill that expands on hover, plus a blue "Done" button — both over the content.
// Single red freehand pen; undo via button or Ctrl/Cmd+Z. Resolves the
// annotated image as a JPEG dataURL, or null on cancel/ESC.
const PEN_COLOR = "#ef4444"; // red
const Z = 2147483000;

export function annotateImage(dataUrl) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => buildOverlay(img, resolve);
        img.onerror = () => resolve(null);
        img.src = dataUrl;
    });
}

function buildOverlay(img, resolve) {
    // Full-viewport canvas painted with the screenshot — overlays the content.
    const canvas = document.createElement("canvas");
    canvas.setAttribute(CAPTURE_EXCLUDE_ATTR, "");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.style.cssText = `position:fixed;inset:0;width:100vw;height:100vh;z-index:${Z};cursor:crosshair;touch-action:none;`;
    const ctx = canvas.getContext("2d");

    const strokes = []; // array of point arrays
    let current = null;

    const redraw = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = PEN_COLOR;
        ctx.lineWidth = Math.max(3, canvas.width / 400);
        ctx.lineCap = ctx.lineJoin = "round";
        for (const s of [...strokes, current].filter(Boolean)) {
            ctx.beginPath();
            s.forEach((p, i) =>
                i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)
            );
            ctx.stroke();
        }
    };
    redraw();

    // Map viewport coords → canvas pixel coords (canvas is stretched to 100vw/vh).
    const toPoint = (e) => ({
        x: (e.clientX / window.innerWidth) * canvas.width,
        y: (e.clientY / window.innerHeight) * canvas.height,
    });
    canvas.onpointerdown = (e) => {
        canvas.setPointerCapture(e.pointerId);
        current = [toPoint(e)];
    };
    canvas.onpointermove = (e) => {
        if (!current) return;
        current.push(toPoint(e));
        redraw();
    };
    canvas.onpointerup = () => {
        if (current && current.length > 1) strokes.push(current);
        current = null;
        redraw();
    };

    // Done — top center, blue, over the content.
    const done = document.createElement("button");
    done.setAttribute(CAPTURE_EXCLUDE_ATTR, "");
    done.textContent = "✓ Done";
    done.style.cssText = `position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:${
        Z + 1
    };border:none;border-radius:10px;padding:9px 24px;font:600 14px system-ui;cursor:pointer;background:#3b82f6;color:#fff;box-shadow:0 6px 20px rgba(59,130,246,.45);`;
    done.onclick = () => finish(canvas.toDataURL("image/jpeg", 0.85));

    // Controls pill — bottom center, over the content. Compact by default
    // (just a dot), expands on hover to reveal Undo / Clear / Cancel.
    const bar = document.createElement("div");
    bar.setAttribute(CAPTURE_EXCLUDE_ATTR, "");
    bar.style.cssText = `position:fixed;bottom:16px;left:50%;transform:translateX(-50%);z-index:${
        Z + 1
    };display:flex;align-items:center;gap:6px;background:rgba(255,255,255,.97);border-radius:999px;padding:6px 8px;box-shadow:0 6px 24px rgba(0,0,0,.18);max-width:44px;overflow:hidden;transition:max-width .25s ease;`;
    bar.onmouseenter = () => (bar.style.maxWidth = "320px");
    bar.onmouseleave = () => (bar.style.maxWidth = "44px");

    // Leading handle (always visible even when collapsed).
    const handle = document.createElement("span");
    handle.textContent = "✏️";
    handle.style.cssText =
        "flex:0 0 auto;width:28px;height:28px;display:inline-flex;align-items:center;justify-content:center;font-size:15px;";
    bar.appendChild(handle);

    const mkBtn = (label, onClick) => {
        const b = document.createElement("button");
        b.textContent = label;
        b.style.cssText =
            "flex:0 0 auto;white-space:nowrap;border:none;border-radius:8px;padding:7px 14px;font:600 13px system-ui;cursor:pointer;background:#f1f5f9;color:#334155;";
        b.onclick = onClick;
        bar.appendChild(b);
    };
    const undo = () => {
        strokes.pop();
        redraw();
    };
    mkBtn("↩ Undo", undo);
    mkBtn("Clear", () => {
        strokes.length = 0;
        redraw();
    });
    mkBtn("Cancel", () => finish(null));

    const onKey = (e) => {
        if (e.key === "Escape") finish(null);
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
            e.preventDefault();
            undo();
        }
    };
    const finish = (result) => {
        window.removeEventListener("keydown", onKey);
        canvas.remove();
        done.remove();
        bar.remove();
        resolve(result);
    };
    window.addEventListener("keydown", onKey);

    document.body.appendChild(canvas);
    document.body.appendChild(done);
    document.body.appendChild(bar);
}
